import p5 from 'p5';

const themes = {
  dark: {
    bg: [14, 16, 15],
    lineColor: [142, 181, 153],
    ambientLight: 35,
    nodeRanges: [
      { roll: 0.55, rR: [100, 170], gR: [165, 210], bR: [120, 170], sR: [4, 14] },
      { roll: 0.85, rR: [80, 130],  gR: [160, 200], bR: [190, 240], sR: [4, 14] },
      { roll: 1.0,  rR: [210, 255], gR: [230, 255], bR: [230, 255], sR: [2, 5]  },
    ]
  },
  light: {
    bg: [242, 237, 227],
    lineColor: [42, 80, 53],
    ambientLight: 80,
    nodeRanges: [
      { roll: 0.55, rR: [30, 75],  gR: [80, 130],  bR: [45, 95],   sR: [4, 14] },
      { roll: 0.85, rR: [20, 55],  gR: [65, 105],  bR: [95, 145],  sR: [4, 14] },
      { roll: 1.0,  rR: [50, 90],  gR: [80, 120],  bR: [55, 100],  sR: [2, 5]  },
    ]
  }
};

let currentTheme = 'dark';
let p5Instance = null;

export function setSketchTheme(theme) {
  currentTheme = theme;
}

export function createSketch(isMobile) {
  return (p) => {
  let nodes = [];
  const NODE_COUNT = isMobile ? 40 : 80;
  let systemRadius;

  let mouseRotX = 0;
  let mouseRotY = 0;

  p.setup = function () {
    p.createCanvas(window.innerWidth, window.innerHeight, p.WEBGL);
    p.pixelDensity(1);
    p.frameRate(isMobile ? 30 : 60);
    systemRadius = p.min(p.width, p.height) * 2.5;
    for (let i = 0; i < NODE_COUNT; i++) {
      nodes.push(new Node());
    }
  };

  p.draw = function () {
    const t = themes[currentTheme];
    p.background(...t.bg);

    if (!isMobile) {
      let targetRotX = p.map(p.mouseY, 0, p.height, 0.18, -0.18);
      let targetRotY = p.map(p.mouseX, 0, p.width, -0.15, 0.15);
      mouseRotX = p.lerp(mouseRotX, targetRotX, 0.025);
      mouseRotY = p.lerp(mouseRotY, targetRotY, 0.025);
    }

    p.rotateX(p.sin(p.frameCount * 0.003) * 0.2 + mouseRotX);
    p.rotateY(p.frameCount * 0.003 + mouseRotY);

    p.ambientLight(t.ambientLight);
    p.directionalLight(...t.lineColor, -0.5, -1, -0.5);

    let breathe = systemRadius * (1 + p.sin(p.frameCount * 0.005) * 0.04);

    const threshold = breathe * 0.22;
    p.strokeWeight(1);
    p.noFill();
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        let d = p5.Vector.dist(nodes[i].pos, nodes[j].pos);
        if (d < threshold) {
          let alpha = p.map(d, 0, threshold, 160, 0);
          p.stroke(...t.lineColor, alpha);
          p.line(
            nodes[i].pos.x, nodes[i].pos.y, nodes[i].pos.z,
            nodes[j].pos.x, nodes[j].pos.y, nodes[j].pos.z
          );
        }
      }
    }

    for (let n of nodes) {
      n.update(breathe);
      n.display();
    }
  };

  p.windowResized = function () {
    p.resizeCanvas(window.innerWidth, window.innerHeight);
    systemRadius = p.min(p.width, p.height) * 2.5;
  };

  class Node {
    constructor() {
      this.theta = p.random(p.TWO_PI);
      this.phi = p.acos(p.random(-1, 1));
      this.pos = p.createVector(0, 0, 0);
      this.wobblePhase = p.random(p.TWO_PI);

      // Pick family index once; store colors for both themes
      let roll = p.random(1);
      const dRanges = themes.dark.nodeRanges;
      const lRanges = themes.light.nodeRanges;
      let idx = dRanges.findIndex(r => roll < r.roll);
      if (idx === -1) idx = dRanges.length - 1;

      const dr = dRanges[idx];
      const lr = lRanges[idx];
      this.colors = {
        dark:  { r: p.random(...dr.rR), g: p.random(...dr.gR), b: p.random(...dr.bR) },
        light: { r: p.random(...lr.rR), g: p.random(...lr.gR), b: p.random(...lr.bR) }
      };
      this.size = p.random(...dr.sR);
    }

    sphericalToCartesian(r) {
      let x = r * p.sin(this.phi) * p.cos(this.theta);
      let y = r * p.cos(this.phi);
      let z = r * p.sin(this.phi) * p.sin(this.theta);
      return p.createVector(x, y, z);
    }

    update(r) {
      this.theta += 0.002 * p.sin(this.phi * 3 + p.frameCount * 0.002 + this.wobblePhase);
      this.phi += 0.001 * p.cos(this.theta * 2 + p.frameCount * 0.001);
      this.pos = this.sphericalToCartesian(r);
    }

    display() {
      p.push();
      p.translate(this.pos.x, this.pos.y, this.pos.z);

      const c = this.colors[currentTheme];
      let pulse = p.sin(p.frameCount * 0.04 + this.wobblePhase);
      let brightness = p.map(pulse, -1, 1, 0.55, 1.0);

      p.ambientMaterial(c.r * brightness, c.g * brightness, c.b * brightness);
      p.emissiveMaterial(
        c.r * 0.12 * brightness,
        c.g * 0.12 * brightness,
        c.b * 0.12 * brightness
      );
      p.noStroke();

      let s = this.size * (1 + pulse * 0.2);
      p.sphere(p.max(1, s), 8, 8);

      p.pop();
    }
  }
  };
}

export function initializeSketch(isMobile) {
  try {
    const container = document.getElementById('p5-container');
    p5Instance = new p5(createSketch(isMobile), container);
  } catch (e) {
    console.error('Error initializing p5:', e);
  }
}
